import { create } from "zustand"
import { TimberProduct } from "../types"

export interface SaleCreateData {
  // Step 1: Basic Information
  name: string
  location_text: string
  description: string
  sale_type: "pay as cut" | "lump sum"

  // Step 2: Map & Boundaries
  boundary_geom?: GeoJSON.Polygon | GeoJSON.MultiPolygon

  // Step 3: Timber Products
  timber_products: TimberProduct[]
  inventory_description?: string

  // Step 4: Terms & Conditions
  bidding_tc: string
  contract_tc: string

  // Step 5: Scheduling
  bid_start_datetime?: Date
  bid_stop_datetime?: Date
  bid_select_datetime?: Date
}

export interface SaleCreateState {
  saleData: SaleCreateData
  currentStep: number
  enabledSteps: boolean[]
  isSubmitting: boolean
  isEditMode: boolean
  editingSaleId: number | null
}

export interface SaleCreateActions {
  // Step navigation
  setCurrentStep: (step: number) => void
  enableStep: (step: number) => void

  // Data updates
  updateSaleData: (data: Partial<SaleCreateData>) => void
  updateBasicInfo: (
    info: Pick<
      SaleCreateData,
      "name" | "location_text" | "description" | "sale_type"
    >
  ) => void
  updateBoundary: (
    boundary: GeoJSON.Polygon | GeoJSON.MultiPolygon | undefined
  ) => void
  updateTimberProducts: (products: TimberProduct[]) => void
  updateInventoryDescription: (inventory_description: string) => void
  updateTerms: (bidding_tc: string, contract_tc: string) => void
  updateScheduling: (
    scheduling: Pick<
      SaleCreateData,
      "bid_start_datetime" | "bid_stop_datetime" | "bid_select_datetime"
    >
  ) => void

  // Form actions
  setSubmitting: (submitting: boolean) => void
  reset: () => void

  // Edit mode actions
  initializeForEdit: (saleId: number, saleData: SaleCreateData) => void
  setEditMode: (isEditMode: boolean) => void

  // Validation
  isStepValid: (step: number) => boolean
}

export const initialSaleData: SaleCreateData = {
  name: "",
  location_text: "",
  description: "",
  sale_type: "lump sum",
  timber_products: [],
  inventory_description: "",
  bidding_tc: "",
  contract_tc: "",
}

export const initialState: SaleCreateState = {
  saleData: initialSaleData,
  currentStep: 0,
  enabledSteps: [true, false, false, false, false],
  isSubmitting: false,
  isEditMode: false,
  editingSaleId: null,
}

export type SaleCreateStoreTypes = SaleCreateState & SaleCreateActions

export const useSaleCreateStore = create<SaleCreateStoreTypes>((set, get) => ({
  ...initialState,

  // Step navigation
  setCurrentStep: (step) => set({ currentStep: step }),
  enableStep: (step) => {
    const prevSteps = get().enabledSteps
    const newSteps = [...prevSteps]
    newSteps[step] = true
    set({ enabledSteps: newSteps })
  },

  // Data updates
  updateSaleData: (data) => {
    const currentData = get().saleData
    set({
      saleData: { ...currentData, ...data },
    })
  },

  updateBasicInfo: (info) => {
    get().updateSaleData(info)
    if (info.name && info.location_text && info.sale_type) {
      get().enableStep(1)
    }
  },

  updateBoundary: (boundary) => {
    get().updateSaleData({ boundary_geom: boundary })
    get().enableStep(2)
  },

  updateTimberProducts: (products) => {
    get().updateSaleData({ timber_products: products })
    if (products.length > 0) {
      get().enableStep(3)
    }
  },

  updateInventoryDescription: (inventory_description) => {
    get().updateSaleData({ inventory_description })
  },

  updateTerms: (bidding_tc, contract_tc) => {
    get().updateSaleData({ bidding_tc, contract_tc })
    if (bidding_tc || contract_tc) {
      get().enableStep(4)
    }
  },

  updateScheduling: (scheduling) => {
    get().updateSaleData(scheduling)
  },

  // Form actions
  setSubmitting: (submitting) => set({ isSubmitting: submitting }),
  reset: () => set(initialState),

  // Edit mode actions
  initializeForEdit: (saleId, saleData) => {
    // Enable all steps since we have complete data
    const allStepsEnabled = [true, true, true, true, true]
    set({
      saleData,
      currentStep: 0,
      enabledSteps: allStepsEnabled,
      isEditMode: true,
      editingSaleId: saleId,
      isSubmitting: false,
    })
  },

  setEditMode: (isEditMode) => set({ isEditMode }),

  // Validation
  isStepValid: (step) => {
    const { saleData } = get()

    switch (step) {
      case 0: // Basic Information & Scheduling
        return !!(
          saleData.name &&
          saleData.location_text &&
          saleData.sale_type &&
          saleData.bid_start_datetime &&
          saleData.bid_stop_datetime &&
          saleData.bid_select_datetime
        )
      case 1: // Map & Boundaries
        return !!saleData.boundary_geom
      case 2: // Timber Products
        return saleData.timber_products.length > 0
      case 3: // Terms & Conditions
        return !!(saleData.bidding_tc && saleData.contract_tc)
      case 4: // Review & Submit - always valid if we got this far
        return true
      default:
        return false
    }
  },
}))
